import { useForm, FormProvider } from "react-hook-form";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";

import ACL_RELATIONSHIPS from "acl-relationships";
import useAuthorized from "hooks/useAuthorized";
import UserService from "services/User";
import { FormCheckbox } from "components/BaseForm";
import { SaveButton } from "components/ui/save-button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "components/ui/card";
import { Spinner } from "components/ui/spinner";

const NOTIFICATIONS = [
  {
    name: "emailNotificationOnDelivery",
    category: "email",
    action: "delivered",
    label: "Be notified on Email Deliveries",
  },
  {
    name: "emailNotificationOnFormResponse",
    category: "form",
    action: "submitted",
    label: "Be notified on Form Responses",
  },
  {
    name: "emailNotificationOnFormError",
    category: "form",
    action: "error",
    label: "Be notified on Form errors",
  },
  {
    name: "importStatusNotification",
    category: "import",
    action: "status",
    label: "Be notified on Import status",
  },
];

const DEFAULT_VALUES = Object.fromEntries(
  NOTIFICATIONS.map(({ name }) => [name, false])
);

export function UserNotifications() {
  const canEdit = useAuthorized([ACL_RELATIONSHIPS.userSettings.edit]);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["notification-settings"],
    queryFn: async () => {
      const results = await Promise.all(
        NOTIFICATIONS.map(({ category, action }) =>
          UserService.getUserAuditLogNotificationSetting(category, action)
        )
      );
      return Object.fromEntries(
        NOTIFICATIONS.map(({ name }, i) => [name, results[i].status || false])
      );
    },
  });

  const form = useForm({
    defaultValues: DEFAULT_VALUES,
    values: data,
    disabled: !canEdit,
  });
  const {
    handleSubmit,
    formState: { isSubmitting, isDirty },
  } = form;

  const onSubmit = async values => {
    try {
      await UserService.editUserAuditLogNotificationSettings(
        NOTIFICATIONS.map(({ category, action, name }) => ({
          category,
          action,
          status: values[name],
        }))
      );
      await refetch();
      toast.success("Notification settings updated");
    } catch (error) {
      toast.error(error.message);
    }
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Card>
          <CardHeader>
            <CardTitle>Notifications</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="tw:flex tw:justify-center tw:py-6">
                <Spinner className="tw:size-6" />
              </div>
            ) : (
              <div className="tw:grid tw:grid-cols-1 tw:gap-4 tw:sm:grid-cols-2">
                {NOTIFICATIONS.map(({ name, label }) => (
                  <FormCheckbox key={name} name={name} label={label} />
                ))}
              </div>
            )}
          </CardContent>

          <CardFooter className="tw:flex tw:justify-end">
            <SaveButton
              type="submit"
              saving={isSubmitting}
              disabled={isLoading || !isDirty}
            />
          </CardFooter>
        </Card>
      </form>
    </FormProvider>
  );
}
